import type { ReactNode } from 'react';
import {
  CircleAlert,
  CircleCheck,
  Info,
  Loader2,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export type CalloutTone = 'error' | 'warning' | 'success' | 'info' | 'neutral';

const ICONS: Record<CalloutTone, LucideIcon> = {
  error: CircleAlert,
  warning: TriangleAlert,
  success: CircleCheck,
  info: Info,
  neutral: Info,
};

const BOX: Record<CalloutTone, string> = {
  error: 'border-destructive/30 bg-destructive/10',
  warning: 'border-warning/30 bg-warning/10',
  success: 'border-success/30 bg-success/10',
  info: 'border-info/30 bg-info/10',
  neutral: 'border-border bg-muted',
};

const TONE_TEXT: Record<CalloutTone, string> = {
  error: 'text-destructive',
  warning: 'text-warning',
  success: 'text-success',
  info: 'text-info',
  neutral: 'text-muted-foreground',
};

export type CalloutProps = {
  tone?: CalloutTone;
  /**
   * `box` (default): tinted message block. `inline`: one coloured line, e.g. under a field.
   */
  size?: 'box' | 'inline';
  title?: ReactNode;
  children?: ReactNode;
  /** Buttons or links under the text (box only). */
  action?: ReactNode;
  /** Replaces the tone's icon; `false` hides it. */
  icon?: LucideIcon | false;
  /** Spinner instead of the icon (a "loading…" message). */
  loading?: boolean;
  /** Defaults to `alert` for errors and `status` for success/loading; pass `null` for none. */
  role?: 'alert' | 'status' | null;
  className?: string;
  id?: string;
  'data-testid'?: string;
};

/**
 * The one message component: errors, warnings, confirmations, information and loading lines.
 * Semantic tones from the theme (`destructive`, `warning`, `success`, `info`), light and dark.
 */
export function Callout({
  tone = 'info',
  size = 'box',
  title,
  children,
  action,
  icon,
  loading = false,
  role,
  className,
  id,
  'data-testid': testId,
}: CalloutProps) {
  const Icon = loading ? Loader2 : icon === false ? null : (icon ?? ICONS[tone]);
  const resolvedRole =
    role === null
      ? undefined
      : (role ??
        (tone === 'error' ? 'alert' : tone === 'success' || loading ? 'status' : undefined));

  if (size === 'inline') {
    return (
      <div
        id={id}
        data-testid={testId}
        data-tone={tone}
        role={resolvedRole}
        className={cn(
          'flex items-start gap-1.5',
          tone === 'neutral' ? 'text-xs text-muted-foreground' : cn('text-sm', TONE_TEXT[tone]),
          className
        )}
      >
        {Icon && (tone !== 'neutral' || loading) && (
          <Icon
            className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', loading && 'animate-spin')}
            aria-hidden
          />
        )}
        <div className="min-w-0 break-words">
          {title && <span className="font-semibold">{title} </span>}
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      id={id}
      data-testid={testId}
      data-tone={tone}
      role={resolvedRole}
      className={cn(
        'flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm',
        BOX[tone],
        className
      )}
    >
      {Icon && (
        <Icon
          className={cn('mt-0.5 h-4 w-4 shrink-0', TONE_TEXT[tone], loading && 'animate-spin')}
          aria-hidden
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 break-words text-foreground">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="whitespace-pre-line">{children}</div>}
        {action && <div className="mt-2 flex flex-wrap items-center gap-2">{action}</div>}
      </div>
    </div>
  );
}
